import { Fragment, useContext, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { UserStore } from "../index";
import {
  faMessage,
  faCalendar,
  faImages,
  faVideo,
  faInfo,
  faDesktop,
  faChartSimple,
  faMapLocationDot,
  faLaptopMedical,
  faEarthOceania,
  faHouseCircleCheck,
  faBuilding,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router";
import { PageContainer } from "../components/page";
import BreadCrumbs from "../components/breadcrumsb";
import { runAuthHandler } from "./util";
import { useColors } from "../provider/colors-provider";

const HOME_SECTIONS = [
  {
    isAdminRequired: false,
    sectionName: "Player Communications",
    pages: [
      {
        title: "Player Messaging",
        description: "Send push notifications to players",
        icon: faMessage,
        pageRoute: "player-communications",
      },
      {
        title: "Schedule Notifications",
        description: "View upcoming player communications",
        icon: faCalendar,
        pageRoute: "scheduled-communications",
      },
    ],
  },
  {
    isAdminRequired: false,
    sectionName: "Mobile App",
    pages: [
      {
        // title: 'Promotional Banners',
        title: "Mobile Banners",
        description: "Provide home screen promotional banners",
        icon: faImages,
        pageRoute: "mobile-banners",
      },
      {
        // title: 'Venue Event Images',
        title: "Mobile Attractors",
        description: "Provide images for venue events",
        icon: faVideo,
        pageRoute: "mobile-attractors",
      },
      {
        // title: 'Additional Information Page',
        title: "Mobile About Us Page",
        description: "Provide links such as terms and conditions",
        icon: faInfo,
        pageRoute: "mobile-about-us",
      },
    ],
  },
  {
    isAdminRequired: false,
    sectionName: "Venue Kiosks",
    pages: [
      {
        title: "Kiosk Promotional Images",
        description: "Provide images to display on venue kiosks",
        icon: faDesktop,
        pageRoute: "kiosk-attractors",
      },
    ],
  },
  {
    isAdminRequired: false,
    sectionName: "Analytics",
    pages: [
      {
        title: "App Analytics",
        description: "View mobile app analytics",
        icon: faChartSimple,
        pageRoute: "analytics",
      },
      {
        title: "Geofence Analytics",
        description: "View geofence analytics",
        icon: faMapLocationDot,
        pageRoute: "geo-analytics",
      },
    ],
  },
  {
    isAdminRequired: true,
    sectionName: "PE Staff Administration Area",
    pages: [
      {
        title: "Kiosk Attractor Health",
        description: "View the health status of all kiosk across venues",
        icon: faLaptopMedical,
        pageRoute: "kiosk-health",
      },
      {
        title: "Geofence Creator",
        description: "Create, edit and delete geofences",
        icon: faEarthOceania,
        pageRoute: "fence-config",
      },
      {
        title: "Venue Geofence Setup",
        description: "Assign geofences to venues",
        icon: faHouseCircleCheck,
        pageRoute: "fence-venue-config",
      },
      {
        title: "Venue Setup & Status ",
        description: "Manage venue setup and status",
        icon: faBuilding,
        pageRoute: "venue-setup-status",
      },
    ],
  },
];

const HomePage = observer(() => {
  const { primary } = useColors();
  const userStore = useContext(UserStore);
  const navigate = useNavigate();
  const pollingTimer = useRef<NodeJS.Timeout>();

  useEffect(() => {
    runAuthHandler({ userStore, navigate });
    return () => {
      if (pollingTimer.current) clearInterval(pollingTimer.current);
    };
  }, [userStore.selectedVenue]);

  return (
    <PageContainer>
      <BreadCrumbs routes={[{ path: "/home", breadcrumbName: "Home" }]} />

      {/* Use a container div for overall layout */}
      <div className="mt-8">
        {HOME_SECTIONS.map(({ isAdminRequired, sectionName, pages }) => (
          // Each section acts as a container for the header and its items, facilitating the desired layout
          <div key={sectionName} className="mt-4">
            {(!isAdminRequired || (isAdminRequired && userStore.isAdmin)) && (
              <Fragment>
                {/* Section Header */}
                <div className="text-2xl font-bold">{sectionName}</div>

                {/* Grid for the items within this section */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pages.map(
                    ({ title, description, icon, pageRoute }, index) => (
                      <div
                        key={sectionName + index}
                        onClick={() => navigate(`/${pageRoute}`)}
                        className="h-52 bg-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:shadow-md transition-all cursor-pointer"
                      >
                        <div className="flex justify-center items-center mb-4 w-10 h-10 rounded-full bg-white lg:h-12 lg:w-12 dark:bg-primary-900">
                          <FontAwesomeIcon
                            color={primary}
                            icon={icon}
                            className="w-6 lg:h-6"
                          />
                        </div>
                        <h3 className="mb-2 text-xl font-bold dark:text-white">
                          {title}
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                          {description}
                        </p>
                      </div>
                    )
                  )}
                </div>
              </Fragment>
            )}
          </div>
        ))}
      </div>
    </PageContainer>
  );
});
export default HomePage;
